import { supabase } from '../supabaseClient';
import type { Grade } from '../types/domain';

export interface UserWorkspaceUiPreferences {
  sidebarFocusGradeId: string | null;
  navIntegratedView: boolean;
  navGradeId: string | null;
}

const EMPTY_PREFS: UserWorkspaceUiPreferences = {
  sidebarFocusGradeId: null,
  navIntegratedView: false,
  navGradeId: null,
};

function normalizeRow(row: {
  sidebar_focus_grade_id?: string | null;
  nav_integrated_view?: boolean | null;
  nav_grade_id?: string | null;
} | null): UserWorkspaceUiPreferences {
  if (!row) return { ...EMPTY_PREFS };
  return {
    sidebarFocusGradeId: row.sidebar_focus_grade_id ?? null,
    navIntegratedView: row.nav_integrated_view === true,
    navGradeId: row.nav_grade_id ?? null,
  };
}

export function resolveGradeIdInList(
  gradeId: string | null | undefined,
  grades: Grade[],
  fallbackGradeId?: string | null
): string | null {
  if (gradeId && grades.some((g) => g.id === gradeId)) return gradeId;
  if (fallbackGradeId && grades.some((g) => g.id === fallbackGradeId)) return fallbackGradeId;
  return grades[0]?.id ?? null;
}

export function resolveNavGradeIdFromPreferences(
  prefs: UserWorkspaceUiPreferences,
  grades: Grade[],
  sidebarFocusGradeId: string | null
): string {
  /** 통합 클래스 보기는 제거됨 — 저장된 통합 플래그가 있어도 유효한 클래스로 해석 */
  const resolved = resolveGradeIdInList(prefs.navGradeId, grades, sidebarFocusGradeId);
  return resolved ?? '';
}

export async function getUserWorkspaceUiPreferences(
  workspaceId: string
): Promise<UserWorkspaceUiPreferences> {
  if (!workspaceId) return { ...EMPTY_PREFS };
  const { data, error } = await supabase
    .from('user_workspace_ui_preferences')
    .select('sidebar_focus_grade_id, nav_integrated_view, nav_grade_id')
    .eq('workspace_id', workspaceId)
    .maybeSingle();
  if (error) {
    if (/relation .* does not exist|Could not find the table/i.test(error.message)) {
      return { ...EMPTY_PREFS };
    }
    throw new Error(error.message);
  }
  return normalizeRow(data);
}

export type SaveUserWorkspaceUiPreferencesPatch = Partial<UserWorkspaceUiPreferences>;

export async function saveUserWorkspaceUiPreferences(
  workspaceId: string,
  patch: SaveUserWorkspaceUiPreferencesPatch
): Promise<void> {
  if (!workspaceId) return;
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw new Error(userError.message);
  if (!user) return;

  const payload: Record<string, unknown> = {
    user_id: user.id,
    workspace_id: workspaceId,
    updated_at: new Date().toISOString(),
  };
  if (patch.sidebarFocusGradeId !== undefined) {
    payload.sidebar_focus_grade_id = patch.sidebarFocusGradeId;
  }
  if (patch.navIntegratedView !== undefined) {
    payload.nav_integrated_view = patch.navIntegratedView;
  }
  if (patch.navGradeId !== undefined) {
    payload.nav_grade_id = patch.navGradeId;
  }

  const { error } = await supabase.from('user_workspace_ui_preferences').upsert(payload, {
    onConflict: 'user_id,workspace_id',
  });
  if (error) {
    if (/relation .* does not exist|Could not find the table/i.test(error.message)) {
      return;
    }
    throw new Error(error.message);
  }
}
